import React from 'react'

import { Box, Stack } from '@mui/material'

import { Card, Skeleton } from '@repo/ui'
import { NARROW_COLUMN_WIDTH } from 'config'
import { Header } from 'components/navigation'

/**
 * Loading state for the announcement editor (§19).
 *
 * FOOTPRINT PARITY, not a shimmer parked in the middle of an empty page: it mirrors
 * every region the loaded form has — the wayfinding row and title (supplied by the real
 * `Header`, so the crumb and the 53px title datum are identical), the two header action
 * buttons, and each of the four cards at the field counts they actually contain. The
 * cards are the SAME `Card` at the SAME `maxWidth: 760` as the form, so nothing shifts
 * when the record lands.
 *
 * `Header` is rendered rather than skeletonised because it already knows what to draw:
 * the page title is static text ("Edit announcement"), not data, so standing a grey bar
 * in for it would be replacing known content with a placeholder.
 *
 * The `NARROW_COLUMN_WIDTH` cap is the SAME one the loaded page's `Column` applies, for
 * the same footprint-parity reason the rest of this file exists: a full-width skeleton
 * resolving into an 1100px centred page is precisely the layout shift §19 is about, and
 * `InvoicePage` shipped exactly that bug before wrapping its loading branch too.
 */
export const InternalAnnouncementEditPageSkeleton = () => (
  <Box sx={{ maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto' }}>
    <Header
      title="Edit announcement"
      breadcrumbsOveride="Edit announcement"
      rightComponents={
        <>
          <Skeleton width={92} height={40} />
          <Skeleton width={84} height={40} />
        </>
      }
    />

    <Stack spacing={4}>
      {/* Announcement — title, the rich-text body, kind. 220px is the editor's own
          `minHeight`, so the tallest field in this card reserves exactly the box the
          toolbar and editing surface will occupy. */}
      <Card title="Announcement" spacing={4}>
        <Stack spacing={4}>
          <FieldSkeleton />
          <FieldSkeleton height={220} />
          <FieldSkeleton />
        </Stack>
      </Card>

      {/* Audience — the roles multi-select plus its two-line helper note. The legacy
          permission notice is NOT reserved: it renders only on the minority of rows that
          carry one, so reserving it would leave a gap on every other row — the inverse of
          the shift this file prevents. */}
      <Card title="Audience" spacing={4}>
        <FieldSkeleton />
        <Skeleton lines={2} sx={{ mt: 2 }} />
      </Card>

      {/* Call to action — label, route, tour key. */}
      <Card title="Call to action" spacing={4}>
        <Stack spacing={4}>
          <FieldSkeleton />
          <FieldSkeleton />
          <FieldSkeleton />
        </Stack>
      </Card>

      {/* Feature requests — the note, the search box, and the 320px scroll pane the
          checklist lives in. Reserving the pane's full height is the point: it is the
          tallest thing on the page, and letting it appear only once the list resolves
          would step the whole form down. */}
      <Card title="Closes these feature requests" spacing={4}>
        <Skeleton lines={2} sx={{ mb: 3 }} />
        <Skeleton height={49} />
        <Skeleton height={320} sx={{ mt: 3 }} />
      </Card>
    </Stack>
  </Box>
)

/** Label bar + control box, at the field chrome's real dimensions (§31's 8px gap). */
const FieldSkeleton = ({ height = 49 }: { height?: number }) => (
  <Box>
    <Skeleton width={110} height={14} sx={{ mb: 2 }} />
    <Skeleton height={height} />
  </Box>
)
